import { useState, useEffect } from 'react'
import { X, Plus, Trash2, FileText } from 'lucide-react'
import { supabase } from './supabase'
import { SERVICE_AREAS, SERVICE_SUBAREAS, STATUSES, APPROVAL_PATHS, isActiveProfile } from './constants'
import { useToast } from './ToastContext'

export default function TaskModal({ task, clients, profiles, currentUser, onClose, onSaved }) {
  const toast = useToast()
  const isEdit = !!task?.id

  const openAttachment = async (a) => {
    // Open the tab during the click so pop-up blockers allow it, then fill it in
    const win = window.open('', '_blank')
    const { data, error } = await supabase.storage.from(a.bucket).createSignedUrl(a.path, 60)
    if (error || !data?.signedUrl) {
      win?.close()
      toast('Could not open file: ' + (error?.message || 'not found'), 'error')
      return
    }
    if (win) win.location.href = data.signedUrl
    else window.location.href = data.signedUrl
  }

  const [form, setForm] = useState({
    title: '',
    description: '',
    client_id: '',
    service_area: 'website',
    service_subarea: '',
    week_number: '',
    assigned_to: '',
    date_due: '',
    status: 'not_started',
    notes: '',
    google_doc_links: [],
    loom_links: [],
    is_onboarding_task: false,
    client_visible: true,
    approval_path: 'internal',
    deliverable_links: task?.deliverable_links || [],
    deliverable_notes: task?.deliverable_notes || '',
    collaborator_ids: task?.collaborator_ids || [],
    is_urgent: task?.is_urgent || false,
    is_recurring: false,
    recurrence_type: 'weekly',
    recurrence_day_of_week: 1,
    recurrence_day_of_month: 1,
    recurrence_interval_days: 7,
    ...task,
    date_due: task?.date_due ? task.date_due.slice(0, 10) : '',
    google_doc_links: task?.google_doc_links || [],
    loom_links: task?.loom_links || [],
  })

  const [newDocLink, setNewDocLink] = useState('')
  const [newLoomLink, setNewLoomLink] = useState('')
  const [newDeliverable, setNewDeliverable] = useState('')
  const [saving, setSaving] = useState(false)

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }))

  const addLink = (field, val, setter) => {
    if (!val.trim()) return
    set(field, [...form[field], val.trim()])
    setter('')
  }

  const removeLink = (field, idx) => {
    set(field, form[field].filter((_, i) => i !== idx))
  }

  const handleSave = async () => {
    if (!form.title.trim()) { toast('Title is required', 'error'); return }
    // client_id is optional for internal tasks
    setSaving(true)
    try {
      const { comment_count, assignee, assigner, client, ...formData } = form
      const payload = {
        ...formData,
        assigned_by: isEdit ? task.assigned_by : currentUser.id,
        week_number: form.week_number ? parseInt(form.week_number) : null,
        date_due: form.date_due || null,
        assigned_to: form.assigned_to || null,
        client_id: form.client_id || null,
        project_id: form.project_id || null,
        project_phase_id: form.project_phase_id || null,
        meeting_note_id: form.meeting_note_id || null,
        recurrence_parent_id: form.recurrence_parent_id || null,
        collaborator_ids: (form.collaborator_ids || []).filter(id => id && id !== ''),
        deliverable_links: (form.deliverable_links || []).filter(l => l && l !== ''),
        google_doc_links: (form.google_doc_links || []).filter(l => l && l !== ''),
        loom_links: (form.loom_links || []).filter(l => l && l !== ''),
        is_urgent: form.is_urgent || false,
      }
      let error
      if (isEdit) {
        ;({ error } = await supabase.from('tasks').update(payload).eq('id', task.id))
      } else {
        ;({ error } = await supabase.from('tasks').insert(payload))
      }
      if (error) throw error

      // Sync to Google Sheets
      let savedId = task?.id
      if (!isEdit) {
        const { data: newTask } = await supabase.from('tasks').select('id').order('created_at', { ascending: false }).limit(1).single()
        savedId = newTask?.id
      }
      if (savedId) {
        supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: savedId } })
      }

      // Notify assignee if status is "ready_for_review" and there's an assigner
      if (form.status === 'ready_for_review' && (isEdit ? task.assigned_by : currentUser.id)) {
        const notifyUser = isEdit ? task.assigned_by : currentUser.id
        if (notifyUser && notifyUser !== currentUser.id) {
          await supabase.from('notifications').insert({
            user_id: notifyUser,
            task_id: task?.id,
            type: 'ready_for_review',
            message: `Task "${form.title}" is ready for your review`,
          })
        }
      }

      toast(isEdit ? 'Task updated' : 'Task created', 'success')
      onSaved()
    } catch (e) {
      toast('Error saving task: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const subareas = SERVICE_SUBAREAS[form.service_area] || []

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div>
            <div className="modal-title">{isEdit ? 'Edit Task' : 'New Task'}</div>
            {isEdit && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              Created {new Date(task.created_at).toLocaleDateString()}
            </div>}
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Task Title *</label>
            <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Keyword research for homepage" />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Client <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
              <select className="form-control" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
                <option value="">🏢 Internal / General (no client)</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Assigned To</label>
              <select className="form-control" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                <option value="">Unassigned</option>
                {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row-3">
            <div className="form-group">
              <label className="form-label">Service Area</label>
              <select className="form-control" value={form.service_area} onChange={e => { set('service_area', e.target.value); set('service_subarea', '') }}>
                {SERVICE_AREAS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Subarea</label>
              <select className="form-control" value={form.service_subarea} onChange={e => set('service_subarea', e.target.value)} disabled={!subareas.length}>
                <option value="">None</option>
                {subareas.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Week #</label>
              <input className="form-control" type="number" min="1" value={form.week_number} onChange={e => set('week_number', e.target.value)} placeholder="e.g. 1" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-control" type="date" value={form.date_due} onChange={e => set('date_due', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={e => set('status', e.target.value)}>
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="What needs to be done?" />
          </div>

          <div className="form-group">
            <label className="form-label">Internal Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Notes visible to team only..." />
          </div>

          {/* Attachments (read-only). Signed contracts land here automatically
              from the proposals app. Files are private: each click gets a
              short-lived link, and storage rules decide who can open them. */}
          {isEdit && task?.attachments?.length > 0 && (
            <div className="form-group">
              <label className="form-label">Attachments</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {task.attachments.map((a, i) => (
                  <button key={i} type="button" className="btn btn-ghost btn-sm" onClick={() => openAttachment(a)}
                    style={{ justifyContent: 'flex-start', gap: 6 }}>
                    <FileText size={14} /> {a.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Google Doc Links */}
          <div className="form-group">
            <label className="form-label">Google Doc Links</label>
            <div className="link-tags">
              {form.google_doc_links.map((link, i) => (
                <span key={i} className="link-tag">
                  <span>{link}</span>
                  <button onClick={() => removeLink('google_doc_links', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input className="form-control" value={newDocLink} onChange={e => setNewDocLink(e.target.value)} placeholder="Paste Google Doc URL..." onKeyDown={e => e.key === 'Enter' && addLink('google_doc_links', newDocLink, setNewDocLink)} />
              <button className="btn btn-ghost btn-sm" onClick={() => addLink('google_doc_links', newDocLink, setNewDocLink)}><Plus size={14} /></button>
            </div>
          </div>

          {/* Loom Links */}
          <div className="form-group">
            <label className="form-label">Loom Videos</label>
            <div className="link-tags">
              {form.loom_links.map((link, i) => (
                <span key={i} className="link-tag loom">
                  <span>{link}</span>
                  <button onClick={() => removeLink('loom_links', i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input className="form-control" value={newLoomLink} onChange={e => setNewLoomLink(e.target.value)} placeholder="Paste Loom URL..." onKeyDown={e => e.key === 'Enter' && addLink('loom_links', newLoomLink, setNewLoomLink)} />
              <button className="btn btn-ghost btn-sm" onClick={() => addLink('loom_links', newLoomLink, setNewLoomLink)}><Plus size={14} /></button>
            </div>
          </div>

          {/* Deliverables */}
          <div className="form-group">
            <label className="form-label">Finished Work / Deliverables <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(Canva, docs, final links)</span></label>
            {form.deliverable_notes !== undefined && (
              <textarea className="form-control" rows={2} value={form.deliverable_notes} onChange={e => set('deliverable_notes', e.target.value)} placeholder="Notes about finished work..." style={{ marginBottom: 8 }} />
            )}
            <div className="link-tags">
              {form.deliverable_links.map((link, i) => (
                <span key={i} className="link-tag" style={{ background: 'var(--lightgreen)', color: '#2e6e3a' }}>
                  <span>{link}</span>
                  <button onClick={() => set('deliverable_links', form.deliverable_links.filter((_, idx) => idx !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, display: 'flex' }}><X size={10} /></button>
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input className="form-control" value={newDeliverable} onChange={e => setNewDeliverable(e.target.value)} placeholder="Paste Canva, Google Doc, or other link..." onKeyDown={e => e.key === 'Enter' && addLink('deliverable_links', newDeliverable, setNewDeliverable)} />
              <button className="btn btn-ghost btn-sm" onClick={() => addLink('deliverable_links', newDeliverable, setNewDeliverable)}><Plus size={14} /></button>
            </div>
          </div>

          {/* Collaborators */}
          <div className="form-group">
            <label className="form-label">Collaborators <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(will be notified on updates)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {profiles.filter(p => isActiveProfile(p) && p.id !== currentUser.id && p.id !== form.assigned_to).map(p => {
                const selected = form.collaborator_ids.includes(p.id)
                return (
                  <button key={p.id} type="button" onClick={() => set('collaborator_ids', selected ? form.collaborator_ids.filter(id => id !== p.id) : [...form.collaborator_ids, p.id])}
                    style={{ padding: '4px 12px', borderRadius: 50, fontSize: 12, cursor: 'pointer', border: '1.5px solid', background: selected ? 'var(--lightpurple)' : '#fff', color: selected ? 'var(--navy)' : 'var(--text-secondary)', borderColor: selected ? '#b0a8cc' : 'var(--border)', fontWeight: selected ? 600 : 400 }}>
                    {p.full_name}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Approval Path</label>
            <select className="form-control" value={form.approval_path} onChange={e => set('approval_path', e.target.value)}>
              {APPROVAL_PATHS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            {form.approval_path && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {APPROVAL_PATHS.find(a => a.value === form.approval_path)?.description}
              </div>
            )}
          </div>

          <hr className="divider" />

          {/* Recurring task settings */}
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 10 }}>
              <input type="checkbox" checked={form.is_recurring} onChange={e => set('is_recurring', e.target.checked)} />
              <span style={{ fontWeight: 600 }}>Recurring Task</span>
            </label>
            {form.is_recurring && (
              <div style={{ background: 'var(--cream)', borderRadius: 8, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Repeats</label>
                  <select className="form-control" value={form.recurrence_type} onChange={e => set('recurrence_type', e.target.value)}>
                    <option value="weekly">Weekly — pick a day</option>
                    <option value="monthly">Monthly — pick a date</option>
                    <option value="custom">Custom — every X days</option>
                  </select>
                </div>
                {form.recurrence_type === 'weekly' && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Day of Week</label>
                    <select className="form-control" value={form.recurrence_day_of_week} onChange={e => set('recurrence_day_of_week', parseInt(e.target.value))}>
                      {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map((d,i) => (
                        <option key={i} value={i}>{d}</option>
                      ))}
                    </select>
                  </div>
                )}
                {form.recurrence_type === 'monthly' && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Day of Month</label>
                    <select className="form-control" value={form.recurrence_day_of_month} onChange={e => set('recurrence_day_of_month', parseInt(e.target.value))}>
                      {Array.from({length: 28}, (_,i) => i+1).map(d => (
                        <option key={d} value={d}>{d === 1 ? '1st' : d === 2 ? '2nd' : d === 3 ? '3rd' : d + 'th'}</option>
                      ))}
                      <option value={31}>Last day of month</option>
                    </select>
                  </div>
                )}
                {form.recurrence_type === 'custom' && (
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Every how many days?</label>
                    <input className="form-control" type="number" min="1" value={form.recurrence_interval_days} onChange={e => set('recurrence_interval_days', parseInt(e.target.value))} placeholder="e.g. 14" />
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  When this task is approved or marked live, the next one will be created automatically.
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.is_onboarding_task} onChange={e => set('is_onboarding_task', e.target.checked)} />
              Onboarding Task
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={!form.client_visible} onChange={e => set('client_visible', !e.target.checked)} />
              Hide from client dashboard
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form.is_urgent} onChange={e => set('is_urgent', e.target.checked)} />
              <span style={{ color: form.is_urgent ? 'var(--red)' : 'inherit', fontWeight: form.is_urgent ? 700 : 400 }}>🔴 Urgent / High Priority</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  )
}